/**
 * Tasarım token'ları — mobil (e-doctor-mobile) ile hizalı.
 *
 * Referanslar:
 *   - components/StyledText/index.js  → 16px body, 22px header
 *   - constants/bottomActionBar.js    → 8px radius, 16px padding
 *   - components/ListItemCard         → 6px card radius
 */

/** @typedef {'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'pageTitle'} FontSizeToken */
/** @typedef {'light' | 'regular' | 'medium' | 'semibold' | 'bold'} FontWeightToken */

export const typography = {
  fontFamily: {
    primary:
      '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  fontSize: {
    xxs: 10,
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    pageTitle: 22,
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
};

/** Mobil borderRadius değerleri */
export const radius = {
  sm: 6,
  md: 8,
  lg: 12,
  pill: 30,
  round: 9999,
};

export const layout = {
  pagePadding: { xs: 16, md: 24 },
  pagePaddingY: { xs: 16, md: 24 },
  sectionGap: 16,
  bottomActionHeight: 56,
  tabBarOffset: 80,
};

/** MUI `sx` prop'larında doğrudan kullanılabilir responsive değerler */
export const layoutTokens = {
  pagePadding: {
    xs: `${layout.pagePadding.xs}px`,
    md: `${layout.pagePadding.md}px`,
  },
  pagePaddingY: {
    xs: `${layout.pagePaddingY.xs}px`,
    md: `${layout.pagePaddingY.md}px`,
  },
  sectionGap: `${layout.sectionGap}px`,
};

/**
 * MUI typography override'ları için hazır config.
 * @param {typeof typography} tokens
 */
export const createMuiTypography = (tokens = typography) => ({
  fontFamily: tokens.fontFamily.primary,
  h1: {
    fontSize: tokens.fontSize.pageTitle,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.tight,
  },
  h2: {
    fontSize: tokens.fontSize.lg,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.tight,
  },
  h3: {
    fontSize: tokens.fontSize.md,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.normal,
  },
  h4: {
    fontSize: tokens.fontSize.md,
    fontWeight: tokens.fontWeight.medium,
    lineHeight: tokens.lineHeight.normal,
  },
  h5: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.medium,
    lineHeight: tokens.lineHeight.normal,
  },
  h6: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.normal,
  },
  subtitle1: {
    fontSize: tokens.fontSize.md,
    fontWeight: tokens.fontWeight.medium,
    lineHeight: tokens.lineHeight.normal,
  },
  subtitle2: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.medium,
    lineHeight: tokens.lineHeight.normal,
  },
  body1: {
    fontSize: tokens.fontSize.md,
    fontWeight: tokens.fontWeight.regular,
    lineHeight: tokens.lineHeight.normal,
  },
  body2: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.regular,
    lineHeight: tokens.lineHeight.normal,
  },
  caption: {
    fontSize: tokens.fontSize.xs,
    fontWeight: tokens.fontWeight.regular,
    lineHeight: tokens.lineHeight.normal,
  },
  overline: {
    fontSize: tokens.fontSize.xxs,
    fontWeight: tokens.fontWeight.medium,
    lineHeight: tokens.lineHeight.normal,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  button: {
    fontSize: 15,
    fontWeight: tokens.fontWeight.semibold,
    lineHeight: tokens.lineHeight.tight,
    textTransform: "none",
  },
});
